import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  GraduationCap, Users, Shield, Crown, ChefHat, Calendar, 
  CreditCard, QrCode, Bell, Heart, Check, ArrowRight,
  Smartphone, Clock, Star, Utensils, Salad, Download, X
} from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lunch-reserve-1/artifacts/i7kakwhy_image.png";

// Imágenes de comida escolar - Fotos profesionales
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1588075592719-8ca3d8075b88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxjaGlsZHJlbiUyMGVhdGluZ3xlbnwwfHx8fDE3NjcxMjg0MTB8MA&ixlib=rb-4.1.0&q=85&w=1200",
  kids: "https://images.unsplash.com/photo-1588075506694-d6ab61c85d2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxjaGlsZHJlbiUyMGVhdGluZ3xlbnwwfHx8fDE3NjcxMjg0MTB8MA&ixlib=rb-4.1.0&q=85&w=800",
  meal1: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxoZWFsdGh5JTIwbHVuY2h8ZW58MHx8fHwxNzY3MTI4NDAzfDA&ixlib=rb-4.1.0&q=85&w=600",
  meal2: "https://images.unsplash.com/photo-1569420067112-b57b4f024595?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxraWRzJTIwbHVuY2h8ZW58MHx8fHwxNzY3MTI4NDE3fDA&ixlib=rb-4.1.0&q=85&w=600",
  meal3: "https://images.pexels.com/photos/8511875/pexels-photo-8511875.jpeg?auto=compress&cs=tinysrgb&w=600",
  family: "https://images.unsplash.com/photo-1588075506694-d6ab61c85d2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxjaGlsZHJlbiUyMGVhdGluZ3xlbnwwfHx8fDE3NjcxMjg0MTB8MA&ixlib=rb-4.1.0&q=85&w=800",
};

// Features del sistema
const FEATURES = [
  {
    icon: Calendar,
    title: "Reserva Semanal",
    description: "Planifica los almuerzos de toda la semana con un solo clic",
    color: "from-amber-400 to-orange-500"
  },
  {
    icon: CreditCard,
    title: "Pago Seguro",
    description: "Paga con tarjeta de crédito o débito a través de Flow.cl",
    color: "from-emerald-400 to-teal-500"
  },
  {
    icon: QrCode,
    title: "Voucher Digital",
    description: "Recibe un QR y PIN único para cada día de almuerzo",
    color: "from-blue-400 to-indigo-500"
  },
  {
    icon: Bell,
    title: "Notificaciones",
    description: "Alertas por email y WhatsApp de tus reservas",
    color: "from-pink-400 to-rose-500"
  },
  {
    icon: Heart,
    title: "Alertas Médicas",
    description: "Registra alergias e intolerancias para cuidar a tus hijos",
    color: "from-red-400 to-pink-500"
  },
  {
    icon: Clock,
    title: "Cambio Flexible",
    description: "Modifica la fecha de tu voucher hasta 24 hrs antes",
    color: "from-violet-400 to-purple-500"
  },
];

// Stats animados
const STATS = [
  { value: "100%", label: "Digital", icon: Smartphone },
  { value: "24/7", label: "Disponible", icon: Clock },
  { value: "⭐", label: "Fácil de usar", icon: Star },
  { value: "🔒", label: "Seguro", icon: Shield },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  
  const heroImages = [IMAGES.hero, IMAGES.meal1, IMAGES.meal3];
  
  useEffect(() => {
    setIsVisible(true);
    
    // Detectar iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);
    
    // Verificar si ya está instalada como PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    
    // Mostrar banner si es móvil y no está instalada
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile && !isStandalone) {
      // Para Android: capturar el evento beforeinstallprompt
      const handleBeforeInstall = (e) => {
        e.preventDefault();
        setInstallPrompt(e);
        setShowInstallBanner(true);
      };
      window.addEventListener('beforeinstallprompt', handleBeforeInstall);
      
      // Para iOS: mostrar instrucciones manuales después de 2 segundos
      if (iOS) {
        setTimeout(() => setShowInstallBanner(true), 2000);
      }
      
      return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    }
    
    // Rotación de imágenes del hero
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const handleInstallClick = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowInstallBanner(false);
      }
      setInstallPrompt(null);
    }
  };

  const dismissBanner = () => {
    localStorage.setItem('pwa_banner_dismissed', Date.now().toString());
    setShowInstallBanner(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-orange-50 overflow-hidden">
      
      {/* Floating Food Elements - Decorative */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-20 left-10 w-20 h-20 bg-amber-200/30 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-orange-200/30 rounded-full blur-2xl animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-40 left-1/4 w-24 h-24 bg-green-200/30 rounded-full blur-2xl animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-20 right-1/3 w-16 h-16 bg-red-200/30 rounded-full blur-2xl animate-pulse" style={{animationDelay: '0.5s'}}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 py-4">
        <nav className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Servicios Catalina" className="h-16 sm:h-20 w-auto" />
            <div className="hidden sm:block">
              <h1 className="font-bold text-gray-800 text-xl">Servicios Catalina</h1>
              <p className="text-sm text-gray-500">Gestión de Almuerzos</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Botón Instalar - Solo móvil */}
            {(installPrompt || isIOS) && (
              <Button 
                variant="outline"
                onClick={isIOS ? () => setShowInstallBanner(true) : handleInstallClick}
                className="text-amber-600 border-amber-400 hover:bg-amber-50 sm:hidden"
                size="sm"
              >
                <Download className="w-4 h-4 mr-1" />
                Instalar
              </Button>
            )}
            <Button 
              variant="ghost" 
              onClick={() => navigate("/admin/login")}
              className="text-gray-600 hover:text-gray-900 hidden sm:flex"
            >
              <Shield className="w-4 h-4 mr-1" />
              Admin
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => navigate("/super-admin/login")}
              className="text-gray-600 hover:text-gray-900 hidden sm:flex"
            >
              <Crown className="w-4 h-4 mr-1" />
              Dueño
            </Button>
          </div>
        </nav>
      </header>

      {/* PWA Install Banner */}
      {showInstallBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-r from-amber-500 to-orange-500 shadow-lg animate-in slide-in-from-bottom duration-300">
          <div className="max-w-xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-md">
                <Download className="w-6 h-6 text-amber-600" />
              </div>
              <div className="text-white">
                <p className="font-bold text-sm sm:text-base">Instala la App Gratis</p>
                {isIOS ? (
                  <p className="text-xs sm:text-sm text-white/80">
                    Toca <span className="font-semibold">Compartir</span> y luego <span className="font-semibold">"Agregar a inicio"</span>
                  </p>
                ) : (
                  <p className="text-xs sm:text-sm text-white/80">Acceso directo sin abrir navegador</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isIOS && installPrompt && (
                <Button 
                  onClick={handleInstallClick}
                  className="bg-white text-amber-600 hover:bg-amber-50 font-bold shadow-md"
                  size="sm"
                >
                  Instalar
                </Button>
              )}
              <button 
                onClick={dismissBanner}
                className="p-2 text-white/80 hover:text-white transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative z-10 px-6 pt-8 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left - Content */}
            <div className={`space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 px-4 py-2 rounded-full">
                <Utensils className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-700">Sistema de Reservas Escolares</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Almuerzos{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500">
                  saludables
                </span>
                {" "}para tus hijos
              </h1>
              
              <p className="text-lg text-gray-600 max-w-lg">
                Reserva, paga y gestiona los almuerzos escolares de forma 100% digital. 
                Simple, seguro y delicioso.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate("/student/login")}
                  className="h-14 px-8 text-lg font-semibold bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-2xl shadow-lg shadow-blue-500/30 transition-all hover:scale-105 hover:shadow-xl"
                >
                  <Users className="w-5 h-5 mr-2" />
                  Soy Apoderado o Alumno
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                <Button 
                  onClick={() => navigate("/teacher/login")}
                  className="h-14 px-8 text-lg font-semibold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-2xl shadow-lg shadow-amber-500/30 transition-all hover:scale-105 hover:shadow-xl"
                >
                  <Users className="w-5 h-5 mr-2 text-white" />
                  Soy Funcionario
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              
              {/* Trust badges */}
              <div className="flex items-center gap-6 pt-4">
                {STATS.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-2xl font-bold text-gray-800">{stat.value}</div>
                    <div className="text-xs text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right - Image Carousel */}
            <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="relative">
                {/* Main Image */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-amber-500/20">
                  {heroImages.map((img, i) => (
                    <img 
                      key={i}
                      src={img}
                      alt="Almuerzo escolar"
                      className={`w-full h-[400px] lg:h-[500px] object-cover transition-opacity duration-1000 ${
                        i === currentImage ? 'opacity-100' : 'opacity-0 absolute inset-0'
                      }`}
                    />
                  ))}
                  
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                  
                  {/* Badge */}
                  <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="font-medium text-gray-800">Reservas abiertas</span>
                    </div>
                  </div>
                </div>
                
                {/* Floating cards */}
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 animate-bounce" style={{animationDuration: '3s'}}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">Reserva confirmada</p>
                      <p className="text-sm text-gray-500">Lunes - Viernes</p>
                    </div>
                  </div>
                </div>
                
                <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl p-4 animate-bounce" style={{animationDuration: '4s', animationDelay: '0.5s'}}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                      <Salad className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">Menú saludable</p>
                      <p className="text-sm text-gray-500">Balanceado y rico</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Image indicators */}
              <div className="flex justify-center gap-2 mt-6">
                {heroImages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImage(i)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      i === currentImage ? 'w-8 bg-amber-500' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 px-6 py-20 bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Todo lo que necesitas en un solo lugar
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Un sistema completo para gestionar los almuerzos de tu colegio de forma eficiente
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, i) => (
              <div 
                key={i}
                className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 overflow-hidden"
                style={{animationDelay: `${i * 100}ms`}}
              >
                {/* Background gradient on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                
                <h3 className="text-xl font-bold text-gray-800 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src={IMAGES.kids} 
                alt="Niños almorzando felices"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-amber-400 to-orange-500 text-white p-6 rounded-2xl shadow-xl">
                <div className="text-4xl font-bold">100%</div>
                <div className="text-sm opacity-90">Satisfacción</div>
              </div>
            </div>
            
            <div className="space-y-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                ¿Cómo funciona?
              </h2>
              
              <div className="space-y-6">
                {[
                  { step: "1", title: "Regístrate", desc: "Crea tu cuenta con tu email y datos del estudiante" },
                  { step: "2", title: "Selecciona", desc: "Elige los días de la semana que deseas reservar" },
                  { step: "3", title: "Paga", desc: "Realiza el pago seguro con tarjeta de crédito/débito" },
                  { step: "4", title: "Recibe", desc: "Recibe tus vouchers con QR único para cada día" },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start group">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0 group-hover:scale-110 transition-transform">
                      {item.step}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 text-lg">{item.title}</h4>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => navigate("/parent/register")}
                className="h-14 px-8 text-lg font-semibold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-2xl shadow-lg shadow-amber-500/30 transition-all hover:scale-105"
              >
                Comenzar ahora
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-5xl mx-auto">
          <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 rounded-3xl p-12 text-center text-white overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-40 h-40 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-60 h-60 bg-white/10 rounded-full translate-x-1/3 translate-y-1/3"></div>
            
            <div className="relative z-10">
              <ChefHat className="w-16 h-16 mx-auto mb-6 opacity-90" />
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                ¿Listo para comenzar?
              </h2>
              <p className="text-lg opacity-90 mb-8 max-w-xl mx-auto">
                Únete a las familias que ya disfrutan de una alimentación escolar organizada y saludable
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate("/student/login")}
                  className="h-14 px-8 text-lg font-semibold bg-white text-amber-600 hover:bg-gray-100 rounded-2xl shadow-lg transition-all hover:scale-105"
                >
                  <GraduationCap className="w-5 h-5 mr-2" />
                  Ingresar como Alumno
                </Button>
                <Button 
                  onClick={() => navigate("/teacher/login")}
                  variant="outline"
                  className="h-14 px-8 text-lg font-semibold border-2 border-white/50 text-white hover:bg-white/10 rounded-2xl transition-all hover:scale-105"
                >
                  <Users className="w-5 h-5 mr-2" />
                  Ingresar como Funcionario
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-12 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="bg-white rounded-xl p-2">
                <img src={LOGO_URL} alt="Servicios Catalina" className="h-14 sm:h-16 w-auto" />
              </div>
              <div>
                <h3 className="font-bold text-xl">Servicios Catalina</h3>
                <p className="text-sm text-gray-400">Gestión de Almuerzos Escolares</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <button onClick={() => navigate("/admin/login")} className="hover:text-white transition-colors">
                Admin
              </button>
              <button onClick={() => navigate("/super-admin/login")} className="hover:text-white transition-colors">
                Super Admin
              </button>
            </div>
            
            <p className="text-sm text-gray-400">
              © 2025 Servicios Catalina. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
